import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  CAPTURES_COLLECTION,
  CAPTURE_PROCESSING_STATUSES,
} from './captureModel';

const ACTIVE_CAPTURE_STATUSES = new Set([
  CAPTURE_PROCESSING_STATUSES.PENDING_PROCESSING,
  CAPTURE_PROCESSING_STATUSES.PROCESSING,
  CAPTURE_PROCESSING_STATUSES.REVIEW_READY,
  CAPTURE_PROCESSING_STATUSES.FAILED,
]);

const RECENT_CAPTURE_LIMIT = 5;
const PENDING_CAPTURE_VISIBLE_MS = 5 * 60 * 1000;

const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return 0;
};

export const shouldShowCaptureStatus = (capture = {}, { nowMs = Date.now() } = {}) => {
  const status = String(capture.status || '').trim();
  if (!ACTIVE_CAPTURE_STATUSES.has(status)) return false;

  if (status !== CAPTURE_PROCESSING_STATUSES.PENDING_PROCESSING) {
    return true;
  }

  const captureTime = toMillis(capture.updatedAt || capture.createdAt);
  if (!captureTime) return true;
  return nowMs - captureTime <= PENDING_CAPTURE_VISIBLE_MS;
};

export const sortCapturesByNewest = (captures = []) => (
  [...captures].sort((a, b) => (
    toMillis(b.createdAt || b.updatedAt) - toMillis(a.createdAt || a.updatedAt)
  ))
);

export const useRecentCaptures = ({ childId = '' } = {}) => {
  const [captures, setCaptures] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!childId) {
      setCaptures([]);
      setError(null);
      return undefined;
    }

    const captureQuery = query(
      collection(db, CAPTURES_COLLECTION),
      where('childId', '==', childId)
    );

    return onSnapshot(
      captureQuery,
      (snapshot) => {
        const nextCaptures = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter(shouldShowCaptureStatus);

        setCaptures(sortCapturesByNewest(nextCaptures).slice(0, RECENT_CAPTURE_LIMIT));
        setError(null);
      },
      (snapshotError) => {
        setError(snapshotError);
      }
    );
  }, [childId]);

  return useMemo(() => ({
    captures,
    error,
  }), [captures, error]);
};

export default useRecentCaptures;
